import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  XMarkIcon, 
  CheckIcon, 
  PlayIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';

const Pricing = ({ variant = 'pricing' }) => {
  const isTrialVariant = variant === 'trial';
  const [billingCycle, setBillingCycle] = useState('annual'); // 'annual' or 'monthly'
  const [numUsers, setNumUsers] = useState(5);
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'enterprise'
  
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const plans = {
    basic: {
      name: 'Básico',
      monthlyPrice: 250,
      annualPrice: 190,
      description: 'Incluye Plan Gratis, más:',
      features: [
        'Espectadores ilimitados gratis',
        'Elementos ilimitados',
        '5 GB de almacenamiento de archivos',
        'Atención prioritaria al cliente',
        'Crea un panel basado en 1 tablero',
        { text: 'Prueba de créditos de IA', badge: 'Nuevo' }
      ]
    },
    standard: {
      name: 'Estándar',
      monthlyPrice: 320,
      annualPrice: 240,
      description: 'Incluye Básico, más:',
      isPopular: true,
      features: [
        'Vistas de cronograma y Gantt',
        'Vista de calendario',
        'Acceso para invitados',
        'Automatizaciones (250 acciones por mes)',
        'Integraciones (250 acciones por mes)',
        'Crea un panel que combine hasta 5 tableros',
        { text: 'Prueba de créditos de IA', badge: 'Nuevo' }
      ]
    },
    pro: {
      name: 'Pro',
      monthlyPrice: 500,
      annualPrice: 380,
      description: 'Incluye Estándar, más:',
      features: [
        'Tableros y documentos privados',
        'Vista de gráfico',
        'Seguimiento de tiempo',
        'Columna fórmula',
        'Columna dependencia',
        'Automatizaciones (25,000 acciones por mes)',
        'Integraciones (25,000 acciones por mes)',
        'Crea un panel que combine hasta 20 tableros',
        { text: 'Prueba de créditos de IA', badge: 'Nuevo' }
      ]
    },
    enterprise: {
      name: 'Enterprise',
      isCustom: true,
      description: 'Para equipos, negocios y organizaciones que quieren maximizar el crecimiento',
      features: [
        'Gestión de cartera',
        'Gestión de recursos',
        'Creador de flujos de trabajo',
        'Automatizaciones e integraciones de escala empresarial',
        'Gobernanza y seguridad de nivel empresarial',
        'Datos con IA y generación de informes avanzados',
        'Crea un panel que combine hasta 50 tableros',
        { text: 'Prueba de créditos de IA', badge: 'Nuevo' }
      ],
      support: 'Soporte de primer nivel',
      onboarding: 'Onboarding personalizado*'
    }
  };

  const calculateTotal = (planKey) => {
    if (planKey === 'enterprise') return null;
    const plan = plans[planKey];
    const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    return price * numUsers;
  };

  const userOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50];

  const handleContinue = () => {
    if (isTrialVariant) {
      navigate('/register');
      return;
    }

    if (selectedPlan === 'enterprise') {
      handleContactSales();
      return;
    }

    const plan = plans[selectedPlan];
    const pricePerUser = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    const monthlyTotal = pricePerUser * numUsers;
    
    // Calcular precio original (para mostrar ahorro)
    const originalPricePerUser = billingCycle === 'annual' 
      ? Math.round(pricePerUser / 0.82) // Revertir el descuento del 18%
      : pricePerUser;
    
    // Calcular totales
    const annualTotal = billingCycle === 'annual' 
      ? monthlyTotal * 12
      : monthlyTotal;
    
    const originalAnnualTotal = billingCycle === 'annual' 
      ? (originalPricePerUser * numUsers) * 12
      : monthlyTotal;
    
    // Calcular ahorro
    const savings = billingCycle === 'annual' 
      ? originalAnnualTotal - annualTotal
      : 0;

    const planData = {
      planName: plan.name,
      numUsers: numUsers,
      billingCycle: billingCycle,
      pricePerUser: pricePerUser,
      total: annualTotal,
      originalPrice: originalAnnualTotal,
      savings: savings,
      monthlyPrice: monthlyTotal
    };

    // Guardar en localStorage como respaldo
    localStorage.setItem('selectedPlan', JSON.stringify(planData));

    // Navegar al checkout con los datos del plan
    navigate('/checkout', { state: planData });
  };

  const handleContactSales = () => {
    toast.success('¡Redirigiendo al formulario de contacto...');
    // Aquí iría la lógica de contacto
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TeamiFlow</h1>
            </Link>

            {/* Theme Toggle & Close */}
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
              <Link
                to="/login"
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {isTrialVariant ? 'Explora los planes y comienza tu prueba gratuita' : 'Elige el plan adecuado para tu equipo'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
              {isTrialVariant
                ? 'Obtén acceso completo a todas las funciones por 30 días sin costo.'
                : 'Tu versión de prueba del plan Pro ha finalizado, elige el plan para tu equipo'}
            </p>
            {isTrialVariant ? (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                No se requiere tarjeta. Puedes cancelar en cualquier momento.
              </span>
            ) : (
              <Link
                to="/dashboard"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center"
              >
                ¿Aún no estás listo? Cambia tu plan a <span className="underline ml-1">Plan Gratis</span>
              </Link>
            )}
          </div>

          {/* Navigation Tabs & Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-soft">
            {/* Tabs */}
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Todos los planes
              </button>
              <button
                onClick={() => setActiveTab('enterprise')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'enterprise'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Negocios y Enterprise
              </button>
            </div>

            {/* User Count Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Ajusta el número de miembros del equipo:
              </span>
              <select
                value={numUsers}
                onChange={(e) => setNumUsers(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {userOptions.map((num) => (
                  <option key={num} value={num}>
                    {num} usuarios
                  </option>
                ))}
              </select>
            </div>

            {/* Billing Cycle Selector */}
            <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
                  billingCycle === 'annual'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Anual{' '}
                <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded ml-1">
                  AHORRA un 18%
                </span>
              </button>
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Mensual
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Basic Plan */}
            {(activeTab === 'all' || selectedPlan === 'basic') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`relative bg-white dark:bg-gray-800 rounded-xl border-2 p-6 shadow-soft transition-all ${
                  selectedPlan === 'basic'
                    ? 'border-primary-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                {/* Radio Button */}
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'basic'}
                    onChange={() => setSelectedPlan('basic')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Básico</h3>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${billingCycle === 'annual' ? plans.basic.annualPrice : plans.basic.monthlyPrice} MXN
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    usuario/Por mes
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-2">
                    Total {calculateTotal('basic')?.toLocaleString('es-MX')} MXN/mes
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Facturado anualmente
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {plans.basic.description}
                  </p>
                  {plans.basic.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckIcon className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                        {typeof feature === 'object' ? (
                          <>
                            <span>{feature.text}</span>
                            {feature.badge && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {feature.badge}
                              </span>
                            )}
                          </>
                        ) : (
                          feature
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {isTrialVariant && (
                  <Link
                    to="/register"
                    className="w-full mt-4 inline-flex justify-center btn-primary py-3 text-sm font-semibold"
                  >
                    Probar gratis
                  </Link>
                )}
              </motion.div>
            )}

            {/* Standard Plan */}
            {(activeTab === 'all' || selectedPlan === 'standard') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`relative bg-white dark:bg-gray-800 rounded-xl border-2 p-6 shadow-soft transition-all ${
                  selectedPlan === 'standard'
                    ? 'border-primary-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                {/* Popular Badge */}
                {plans.standard.isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Más popular
                    </span>
                  </div>
                )}

                {/* Radio Button */}
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'standard'}
                    onChange={() => setSelectedPlan('standard')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Estándar</h3>
                </div>

                {/* Info Button */}
                <button className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <PlayIcon className="h-3 w-3" />
                  </div>
                  <span>¿Por qué Estándar?</span>
                </button>

                {/* Price */}
                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${billingCycle === 'annual' ? plans.standard.annualPrice : plans.standard.monthlyPrice} MXN
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    usuario/Por mes
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-2">
                    Total {calculateTotal('standard')?.toLocaleString('es-MX')} MXN/mes
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Facturado anualmente
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {plans.standard.description}
                  </p>
                  {plans.standard.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckIcon className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                        {typeof feature === 'object' ? (
                          <>
                            <span>{feature.text}</span>
                            {feature.badge && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {feature.badge}
                              </span>
                            )}
                          </>
                        ) : (
                          feature
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {isTrialVariant && (
                  <Link
                    to="/register"
                    className="w-full mt-4 inline-flex justify-center btn-primary py-3 text-sm font-semibold"
                  >
                    Probar gratis
                  </Link>
                )}
              </motion.div>
            )}

            {/* Pro Plan */}
            {(activeTab === 'all' || selectedPlan === 'pro') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`relative bg-white dark:bg-gray-800 rounded-xl border-2 p-6 shadow-soft transition-all ${
                  selectedPlan === 'pro'
                    ? 'border-primary-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                {/* Radio Button */}
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'pro'}
                    onChange={() => setSelectedPlan('pro')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Pro</h3>
                </div>

                {/* Info Button */}
                <button className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <PlayIcon className="h-3 w-3" />
                  </div>
                  <span>¿Por qué Pro?</span>
                </button>

                {/* Price */}
                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${billingCycle === 'annual' ? plans.pro.annualPrice : plans.pro.monthlyPrice} MXN
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    usuario/Por mes
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-2">
                    Total {calculateTotal('pro')?.toLocaleString('es-MX')} MXN/mes
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Facturado anualmente
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {plans.pro.description}
                  </p>
                  {plans.pro.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckIcon className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                        {typeof feature === 'object' ? (
                          <>
                            <span>{feature.text}</span>
                            {feature.badge && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {feature.badge}
                              </span>
                            )}
                          </>
                        ) : (
                          feature
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {isTrialVariant && (
                  <Link
                    to="/register"
                    className="w-full mt-4 inline-flex justify-center btn-primary py-3 text-sm font-semibold"
                  >
                    Probar gratis
                  </Link>
                )}
              </motion.div>
            )}

            {/* Enterprise Plan */}
            {(activeTab === 'all' || activeTab === 'enterprise' || selectedPlan === 'enterprise') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`relative bg-white dark:bg-gray-800 rounded-xl border-2 p-6 shadow-soft transition-all ${
                  selectedPlan === 'enterprise'
                    ? 'border-primary-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                {/* Radio Button */}
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'enterprise'}
                    onChange={() => setSelectedPlan('enterprise')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Enterprise</h3>
                </div>

                {/* Price Icon */}
                <div className="mb-4">
                  <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900 dark:text-white text-center">
                    precio personalizado
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center">
                  {plans.enterprise.description}
                </p>

                {/* Contact Button */}
                <button
                  onClick={handleContactSales}
                  className="w-full flex items-center justify-center space-x-2 btn-secondary py-3 mb-6"
                >
                  <EnvelopeIcon className="h-5 w-5" />
                  <span>Contáctanos</span>
                </button>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Incluye Pro, más:
                  </p>
                  {plans.enterprise.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckIcon className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                        {typeof feature === 'object' ? (
                          <>
                            <span>{feature.text}</span>
                            {feature.badge && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {feature.badge}
                              </span>
                            )}
                          </>
                        ) : (
                          feature
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Support & Onboarding */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                  <div className="flex items-center space-x-2">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-primary-600" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {plans.enterprise.support}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-primary-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {plans.enterprise.onboarding}
                    </span>
                  </div>
                </div>

                {isTrialVariant && (
                  <Link
                    to="/register"
                    className="w-full mt-4 inline-flex justify-center btn-primary py-3 text-sm font-semibold"
                  >
                    Probar gratis
                  </Link>
                )}
              </motion.div>
            )}
          </div>

          {/* Footer Section */}
          {!isTrialVariant ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-soft flex flex-col lg:flex-row items-center justify-between gap-4">
              {/* Guarantee */}
              <div className="flex items-center space-x-2">
                <span className="text-2xl">⭐⭐⭐</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Los primeros 30 días Garantía de devolución proporcional del dinero
                </span>
              </div>

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                className="btn-primary px-8 py-3 text-lg font-semibold"
              >
                Continuar
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-soft flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🎉</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Disfruta de 30 días gratis con todas las funciones activas.
                </span>
              </div>
              <Link
                to="/register"
                className="btn-primary px-8 py-3 text-lg font-semibold"
              >
                Registrarme gratis
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;

